<script setup>
import DemoProfilesSlideshow from "@/Components/DemoProfilesSlideshow.vue";
import EarningsCalculator from "@/Components/EarningsCalculator.vue";
import AdvancedAnalytics from "@/Components/Front/Partials/Features/AdvancedAnalytics.vue";
import DigitalCardsFeature from "@/Components/Front/Partials/Features/DigitalCardsFeature.vue";
import SmartIntegrations from "@/Components/Front/Partials/Features/SmartIntegrations.vue";
import FeaturesShareSection from "@/Components/Front/Partials/FeaturesShareSection.vue";
import HeroContent from "@/Components/Front/Partials/HeroContent.vue";
import { useHomeForm } from "@/composables/useHomeForm";
import Front from "@/Layouts/Front.vue";
import { getSvgForPlatform, initializeIconService } from "@/services/iconService";
import { Head, Link, usePage } from "@inertiajs/vue3";
import "swiper/swiper-bundle.css";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

const integrations = [
    { slug: "tiktok", name: "TikTok" },
    { slug: "youtube", name: "YouTube" },
    { slug: "instagram", name: "Instagram" },
    { slug: "facebook", name: "Facebook" },
    { slug: "threads", name: "Threads" },
    { slug: "linkedin", name: "LinkedIn" },
    { slug: "twitch", name: "Twitch" },
    { slug: "spotify", name: "Spotify" },
    { slug: "apple-music", name: "Apple Music" },
    { slug: "soundcloud", name: "SoundCloud" },
    { slug: "calendly", name: "Calendly" },
];

const integrationIcons = ref([]);
const integrationsViewport = ref(null);
const integrationsTrack = ref(null);
const isIntegrationsCarousel = ref(false);
const canScrollIntegrationsPrev = ref(false);
const canScrollIntegrationsNext = ref(false);
const page = usePage();
let integrationsResizeObserver;

const digitalCardCtaHref = computed(() =>
    page.props.auth?.user
        ? route("dashboard.digital-cards.create")
        : route("signup.pro")
);

const updateIntegrationCarouselControls = () => {
    const viewport = integrationsViewport.value;

    if (!viewport || !isIntegrationsCarousel.value) {
        canScrollIntegrationsPrev.value = false;
        canScrollIntegrationsNext.value = false;
        return;
    }

    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;
    canScrollIntegrationsPrev.value = viewport.scrollLeft > 4;
    canScrollIntegrationsNext.value = maxScrollLeft - viewport.scrollLeft > 4;
};

const measureIntegrationsLayout = () => {
    const viewport = integrationsViewport.value;
    const track = integrationsTrack.value;

    if (!viewport || !track) {
        return;
    }

    const items = Array.from(track.children);

    if (!items.length) {
        isIntegrationsCarousel.value = false;
        updateIntegrationCarouselControls();
        return;
    }

    const trackStyles = window.getComputedStyle(track);
    const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || "0");
    const totalItemWidth =
        items.reduce(
            (sum, item) => sum + item.getBoundingClientRect().width,
            0
        ) +
        gap * Math.max(items.length - 1, 0);
    const shouldUseCarousel = totalItemWidth > viewport.clientWidth + 1;

    isIntegrationsCarousel.value = shouldUseCarousel;

    if (!shouldUseCarousel && viewport.scrollLeft !== 0) {
        viewport.scrollTo({ left: 0, behavior: "auto" });
    }

    updateIntegrationCarouselControls();
};

const scheduleIntegrationsLayoutMeasure = () => {
    window.requestAnimationFrame(() => {
        measureIntegrationsLayout();
    });
};

const scrollIntegrations = (direction) => {
    const viewport = integrationsViewport.value;

    if (!viewport) {
        return;
    }

    viewport.scrollBy({
        left: direction * viewport.clientWidth * 0.8,
        behavior: "smooth",
    });
};
// Props
const props = defineProps({
    urlName: {
        type: String,
        default: "",
    },
    stylePreviews: {
        type: Array,
        default: () => [],
    },
    signupRouteName: {
        type: String,
        default: "signup.details",
    },
    signupButtonText: {
        type: String,
        default: "Sign Up Free",
    },
});

// Use the shared composable
const {
    form,
    submit,
    initializeMouseEffects,
    initializeProductShowcase,
    cleanupScrollHandler,
} = useHomeForm(props.signupRouteName);

// Lifecycle hooks
onMounted(async () => {
    await initializeIconService();
    integrationIcons.value = integrations.map((i) => ({
        ...i,
        mono: getSvgForPlatform(i.slug, "mono"),
        color: getSvgForPlatform(i.slug, "default"),
    }));
    await nextTick();
    scheduleIntegrationsLayoutMeasure();

    integrationsResizeObserver = new ResizeObserver(() => {
        scheduleIntegrationsLayoutMeasure();
    });

    if (integrationsViewport.value) {
        integrationsResizeObserver.observe(integrationsViewport.value);
    }

    initializeMouseEffects();
    initializeProductShowcase();
});

onBeforeUnmount(() => {
    integrationsResizeObserver?.disconnect();

    if (cleanupScrollHandler.value) {
        cleanupScrollHandler.value();
    }
});
</script>
<template>
    <Head
        title="ALink in bio tool: Everything you are, in one simple link | Allmyonline"
    />

    <Front
        :onSubmit="submit"
        v-model:claimLink="form.claim_link"
        v-model:processing="form.processing"
        :signupRouteName="props.signupRouteName"
        :signupButtonText="props.signupButtonText"
    >
        <!-- Hero Section -->
        <section class="hero">
            <HeroContent
                :claim-link="form.claim_link"
                @update:claim-link="form.claim_link = $event"
                :processing="form.processing"
                :on-submit="submit"
            />
            <div class="d-flex justify-content-center align-items-center">
                <DemoProfilesSlideshow />
            </div>
        </section>

        <!-- Integrations Row -->
        <section class="integrations-row">
            <h2 class="integrations-row__title text-center">
                Connect your favorite accounts
            </h2>
            <div
                class="integrations-row__carousel"
                :class="{
                    'integrations-row__carousel--active':
                        isIntegrationsCarousel,
                }"
            >
                <button
                    v-if="isIntegrationsCarousel"
                    type="button"
                    class="integrations-row__nav"
                    :disabled="!canScrollIntegrationsPrev"
                    aria-label="Show previous integrations"
                    @click="scrollIntegrations(-1)"
                >
                    &lt;
                </button>

                <div
                    ref="integrationsViewport"
                    class="integrations-row__viewport"
                    :class="{
                        'integrations-row__viewport--carousel':
                            isIntegrationsCarousel,
                    }"
                    @scroll="updateIntegrationCarouselControls"
                >
                    <div
                        ref="integrationsTrack"
                        class="integrations-row__list"
                        :class="{
                            'integrations-row__list--carousel':
                                isIntegrationsCarousel,
                        }"
                    >
                        <div
                            v-for="icon in integrationIcons"
                            :key="icon.slug"
                            class="integrations-row__item"
                            :aria-label="icon.name"
                            :title="`allmy.online x ${icon.name}`"
                        >
                            <span
                                class="integrations-row__icon integrations-row__icon--mono"
                                v-html="icon.mono"
                            ></span>
                            <span
                                class="integrations-row__icon integrations-row__icon--color"
                                v-html="icon.color"
                            ></span>
                            <span class="integrations-row__label">
                                allmy.online &times; {{ icon.name }}
                            </span>
                        </div>
                    </div>
                </div>

                <button
                    v-if="isIntegrationsCarousel"
                    type="button"
                    class="integrations-row__nav"
                    :disabled="!canScrollIntegrationsNext"
                    aria-label="Show next integrations"
                    @click="scrollIntegrations(1)"
                >
                    &gt;
                </button>
            </div>
        </section>

        <section
            class="features home-card-showcase w-100"
            aria-label="Digital cards showcase"
        >
            <div class="feature" style="max-width: none">
                <h2 class="text-center">Everything about you. One scan away.</h2>
                <h3 class="text-center">
                    One QR code for all your links, always up to date.
                </h3>
                <Link
                    :href="digitalCardCtaHref"
                    class="home-card-showcase__cta amo-btn amo-btn--primary fs-5"
                >
                    Create a digital card
                </Link>
            </div>
            <div class="home-card-showcase__shell">
                <div
                    class="home-card-showcase__device-cluster home-card-showcase__device-cluster--left"
                >
                    <div class="home-card-showcase__phone">
                        <img
                            src="/template/allmyonline/assets/img/hp-styles/onyx-digital-card.png"
                            alt="Digital card preview on phone"
                            class="home-card-showcase__screen"
                        />
                        <img
                            src="/template/allmyonline/assets/iphone-16-pro-frame-nonotch.png"
                            alt=""
                            class="home-card-showcase__frame"
                        />
                    </div>

                    <div class="home-card-showcase__watch" aria-hidden="true">
                        <div class="home-card-showcase__watch-screen">
                            <img
                                src="/template/allmyonline/assets/img/hp-styles/onyx-watch-qr-code.png"
                                alt=""
                                class="home-card-showcase__watch-preview"
                            />
                        </div>
                        <img
                            src="/template/allmyonline/assets/apple-watch-bezel.png"
                            alt=""
                            class="home-card-showcase__watch-frame"
                        />
                    </div>
                </div>

                <div
                    class="home-card-showcase__device-cluster home-card-showcase__device-cluster--center"
                >
                    <div
                        class="home-card-showcase__phone home-card-showcase__phone--center"
                    >
                        <img
                            src="/template/allmyonline/assets/img/hp-styles/onyxphysique.png"
                            alt="Digital card website preview on phone"
                            class="home-card-showcase__screen"
                        />
                        <img
                            src="/template/allmyonline/assets/iphone-16-pro-frame-nonotch.png"
                            alt=""
                            class="home-card-showcase__frame"
                        />
                    </div>
                </div>

                <div
                    class="home-card-showcase__device-cluster home-card-showcase__device-cluster--right"
                >
                    <div class="home-card-showcase__phone">
                        <img
                            src="/template/allmyonline/assets/img/hp-styles/onyx-contact.jpg"
                            alt="Digital contact card preview on phone"
                            class="home-card-showcase__screen"
                        />
                        <img
                            src="/template/allmyonline/assets/iphone-16-pro-frame-nonotch.png"
                            alt=""
                            class="home-card-showcase__frame"
                        />
                    </div>
                </div>
            </div>
        </section>

        <section
            class="features feature-flex dark-features mt-16 md:gap-8 lg:gap-16"
        >
            <div class="feature">
                <h2>This is how much you could be earning with Allmy.online</h2>
                <p class="text-lg">
                    With our referral program, you earn every time someone signs
                    up for Allmy.online using your link. If you have a good
                    following, you could earn a salary just from your referrals.
                </p>
            </div>
            <EarningsCalculator />
        </section>


    </Front>
</template>

<style scoped>
.swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
}

/* For mobile screens - Display 1 slide per view */
@media (max-width: 640px) {
    .swiper-slide {
        width: 100% !important; /* Make each slide take full width */
    }
}

.features {
    padding: 50px 0;
}

.home-card-showcase {
    padding: 1.5rem 1rem 3rem;
}

.home-card-showcase .feature {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.home-card-showcase__cta {
    margin: 1.25rem auto 0;
}

.home-card-showcase__shell {
    max-width: 71rem;
    margin: 0 auto;
    padding: 1.5rem 1rem 1.75rem;
    border-radius: 2rem;
    background: linear-gradient(180deg, #eee8e1 0%, #e9e2db 100%);
    display: grid;
    gap: 1.5rem;
    justify-items: center;
    overflow: hidden;
}

.home-card-showcase__device-cluster {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
}

.home-card-showcase__device-cluster--left {
    flex-direction: column;
    align-items: center;
    gap: 0;
    width: fit-content;
    margin: 0 auto;
    padding-bottom: 0.85rem;
}

.home-card-showcase__phone {
    position: relative;
    width: min(15.5rem, 82vw);
    aspect-ratio: 1109 / 2294;
    flex: 0 0 auto;
}

.home-card-showcase__phone--center {
    width: min(15.5rem, 82vw);
}

.home-card-showcase__screen {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    padding: 0.4rem;
    border-radius: 2rem;
    object-fit: cover;
    object-position: top center;
    z-index: 1;
}

.home-card-showcase__frame,
.home-card-showcase__watch-frame {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    z-index: 2;
    pointer-events: none;
}

.home-card-showcase__watch {
    position: absolute;
    right: -0.4rem;
    bottom: 0;
    width: min(6.5rem, 34vw);
    aspect-ratio: 600 / 940;
    z-index: 3;
}

.home-card-showcase__watch-screen {
    position: absolute;
    inset: 18.5% 9.5% 18.5% 10%;
    border-radius: 1.6rem;
    overflow: hidden;
    z-index: 1;
        background: white;
    padding: 0.6rem;
}

.home-card-showcase__watch-preview {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

.integrations-row {
    padding: 3rem 1rem;
}

.integrations-row__title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 1.5rem;
}

.integrations-row__carousel {
    max-width: 1200px;
    margin: 0 auto;
}

.integrations-row__carousel--active {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 0.75rem;
    align-items: center;
}

.integrations-row__viewport {
    width: 100%;
}

.integrations-row__viewport--carousel {
    overflow-x: auto;
    scroll-behavior: smooth;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: 0.25rem 0;
}

.integrations-row__viewport--carousel::-webkit-scrollbar {
    display: none;
}

.integrations-row__list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
}

.integrations-row__list--carousel {
    width: max-content;
    flex-wrap: nowrap;
    justify-content: flex-start;
}

.integrations-row__item {
    position: relative;
    width: 4rem;
    height: 4rem;
    flex: 0 0 auto;
    border: 1px solid var(--border, #e5e7eb);
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text, #1f2937);
    background: var(--surface, #fff);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease,
        border-color 0.2s ease;
}

.integrations-row__list--carousel .integrations-row__item {
    scroll-snap-align: center;
}

.integrations-row__item:hover {
    transform: translateY(-2px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.08);
    border-color: var(--text, #1f2937);
    z-index: 2;
}

.integrations-row__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    transition: opacity 0.2s ease;
}

.integrations-row__icon--mono {
    opacity: 0;
}

.integrations-row__icon--color {
    position: absolute;
    inset: 0;
    margin: auto;
    opacity: 1;
}

.integrations-row__item:hover .integrations-row__icon--mono {
    opacity: 1;
}

.integrations-row__item:hover .integrations-row__icon--color {
    opacity: 0;
}

.integrations-row__icon :deep(svg) {
    width: 100%;
    height: 100%;
}

.integrations-row__label {
    position: absolute;
    top: calc(100% + 0.75rem);
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--text, #1f2937);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
}

.integrations-row__item:hover .integrations-row__label {
    opacity: 1;
}

.integrations-row__list--carousel .integrations-row__label {
    display: none;
}

.integrations-row__nav {
    width: 2.75rem;
    height: 2.75rem;
    border: 1px solid var(--border, #e5e7eb);
    border-radius: 999px;
    background: var(--surface, #fff);
    color: var(--text, #1f2937);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1;
    transition: opacity 0.2s ease, transform 0.2s ease,
        border-color 0.2s ease;
}

.integrations-row__nav:disabled {
    opacity: 0.4;
    cursor: default;
}

.integrations-row__nav:not(:disabled):hover {
    transform: translateY(-1px);
    border-color: var(--text, #1f2937);
}

@media (min-width: 768px) {
    .home-card-showcase {
        padding: 2.25rem 1.5rem 4rem;
    }

    .home-card-showcase__shell {
        padding: 3rem 2.5rem 2.5rem;
        grid-template-columns: 1fr 1fr 1fr;
        align-items: end;
        gap: 1.5rem;
    }

    .home-card-showcase__phone {
        width: min(16rem, 24vw);
    }

    .home-card-showcase__phone--center {
        width: min(16rem, 24vw);
    }

    .home-card-showcase__device-cluster--left {
        min-height: 100%;
        align-items: flex-start;
        width: 100%;
        margin: 0;
        padding-bottom: 0;
    }

    .home-card-showcase__watch {
        position: absolute;
        right: -0.35rem;
        bottom: -0.55rem;
        width: min(7.9rem, 11vw);
        margin-top: 0;
    }

    .integrations-row__title {
        font-size: 1.5rem;
    }

    .integrations-row__item {
        width: 4.5rem;
        height: 4.5rem;
    }
}

@media (min-width: 1024px) {
    .home-card-showcase__shell {
        padding: 3.5rem 3.25rem 3rem;
        gap: 2rem;
    }

    .home-card-showcase__phone {
        width: 16.2rem;
    }

    .home-card-showcase__phone--center {
        width: 16.2rem;
    }

    .home-card-showcase__watch {
        right: -0.8rem;
        bottom: -0.75rem;
        width: 8.7rem;
    }
}
</style>
